// src/components/topNavbarTool/ProfileDropdown.jsx
import { useRef, useEffect, useState } from "react";
import { User, RefreshCw, KeyRound, LogOut, X, Eye, EyeOff, Loader2 } from "lucide-react";
import "../../styles/profileDropdown.css";

/* ─────────────────────────────────────────────
   Update Password Modal
───────────────────────────────────────────── */
function UpdatePasswordModal({ isOpen, onClose }) {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd]         = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState(false);
  /* reset state when modal opens/closes */
  useEffect(() => {
    if (!isOpen) {
      setCurrentPwd("");
      setNewPwd("");
      setError("");
      setSuccess(false);
      setLoading(false);
    }
  }, [isOpen]);
  /* close on Escape */
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);
  async function handleSubmit() {
  setError("");
  if (!currentPwd.trim() || !newPwd.trim()) {
    setError("Both fields are required.");
    return;
  }
  if (newPwd.length < 6) {
    setError("New password must be at least 6 characters.");
    return;
  }
  setLoading(true);
  try {
    const response = await fetch(
      "http://127.0.0.1:8000/api/update-password/",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({
          current_password: currentPwd,
          new_password: newPwd,
        }),
      }
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Password update failed");
    }
    setSuccess(true);
    setTimeout(() => {
      onClose();
    }, 1500);
  } catch (err) {
    setError(err.message || "Failed to update password.");
  } finally {
    setLoading(false);
  }
}
  if (!isOpen) return null;
  return (
    <div className="pwd-backdrop" onClick={onClose}>
      <div
        className="pwd-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwd-title"
      >
        {/* Header */}
        <div className="pwd-header">
          <h2 id="pwd-title" className="pwd-title">Update Password</h2>
          <button className="pwd-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="pwd-body">
          {success ? (
            <div className="pwd-success">
              <span className="pwd-success-icon">✓</span>
              Password updated successfully!
            </div>
          ) : (
            <>
              <div className="pwd-fields-row">
                {/* Current Password */}
                <div className="pwd-field-wrapper">
                  <input
                    type={showCurrent ? "text" : "password"}
                    className="pwd-input"
                    placeholder="Current Password *"
                    value={currentPwd}
                    onChange={(e) => setCurrentPwd(e.target.value)}
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    className="pwd-eye-btn"
                    type="button"
                    onClick={() => setShowCurrent((v) => !v)}
                    tabIndex={-1}
                    aria-label="Toggle visibility"
                  >
                    {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>

                {/* New Password */}
                <div className="pwd-field-wrapper">
                  <input
                    type={showNew ? "text" : "password"}
                    className="pwd-input"
                    placeholder="New Password *"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                    onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                  />
                  <button
                    className="pwd-eye-btn"
                    type="button"
                    onClick={() => setShowNew((v) => !v)}
                    tabIndex={-1}
                    aria-label="Toggle visibility"
                  >
                    {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>

                {/* Submit */}
                <button
                  className="pwd-submit-btn"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading
                    ? <Loader2 size={15} className="pwd-spinner" />
                    : "SUBMIT"}
                </button>
              </div>
              {error && <p className="pwd-error">{error}</p>}
            </>
          )}
        </div>
        {/* Footer */}
        <div className="pwd-footer">
          <button className="pwd-close-text-btn" onClick={onClose}>
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
}
/* ─────────────────────────────────────────────
   Profile Dropdown
───────────────────────────────────────────── */
export default function ProfileDropdown({
  isOpen,
  onClose,
  userName  = "Dr Ritu",
  userEmail = "ritu@dicoms.in",
  onLogout,
}) {
  const dropdownRef = useRef(null);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [refreshing,   setRefreshing]   = useState(false);
  /* Close on outside click */
  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen, onClose]);
  /* Close on Escape */
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);
  /* Hard Refresh */
  function handleHardRefresh() {
    setRefreshing(true);
    onClose?.();
    setTimeout(() => {
      window.location.reload(true); // true = bypass cache (hard refresh)
    }, 150);
  }
  /* Update Password */
  function handleUpdatePassword() {
    onClose?.();
    setPwdModalOpen(true);
  }
  /* Logout */
  async function handleLogout() {
    onClose?.();
    try {
      const refresh_token = localStorage.getItem("refresh_token");
      await fetch("/api/logout/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("access_token") && {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          }),
        },
        body: JSON.stringify({ refresh_token }),
      });
    } catch (err) {
      console.error("Logout API error:", err);
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      if (typeof onLogout === "function") {
        onLogout(); 
      } else {
        window.location.href = "/";
      }
    }
  }
  return (
    <>
      {isOpen && (
        <div ref={dropdownRef} className="profile-dropdown">
          {/* User info section */}
          <div className="profile-dropdown-header">
            <div className="profile-dropdown-avatar">
              <User size={16} color="white" />
            </div>
            <div className="profile-dropdown-info">
              <span className="profile-dropdown-name">{userName}</span>
              <span className="profile-dropdown-email">{userEmail}</span>
            </div>
          </div>
          <div className="profile-dropdown-divider" />
          {/* Menu items */}
          <button
            className="profile-dropdown-item"
            onClick={handleHardRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={14} className={`profile-item-icon${refreshing ? " spinning" : ""}`} />
            Hard Refresh
          </button>
          <button
            className="profile-dropdown-item"
            onClick={handleUpdatePassword}
          >
            <KeyRound size={14} className="profile-item-icon" />
            Update Password
          </button>
          
          <div className="profile-dropdown-divider" />
          <button
            className="profile-dropdown-item profile-dropdown-item--logout"
            onClick={handleLogout}
          >
            <LogOut size={14} className="profile-item-icon" />
            Logout
          </button>
        </div>
      )}
      <UpdatePasswordModal
        isOpen={pwdModalOpen}
        onClose={() => setPwdModalOpen(false)}
      />
    </>
  );
}